/**
 * OnboardingChecklist — shows setup progress as a step-by-step checklist.
 *
 * When all steps are complete, collapses to a compact "Setup Complete" state.
 * The active step (first incomplete) gets a CTA button.
 */

import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Button } from '@material-ui/core';
import { CheckCircle, RadioButtonUnchecked } from '@material-ui/icons';
import clsx from 'clsx';
import { ActionRoute } from '../../operatorState';
import { resolveActionRoute } from '../../resolveActionRoute';
import { ChecklistStepId } from '../../deriveChecklistSteps';
import styles from './OnboardingChecklist.module.scss';

export default function OnboardingChecklist({
  checklist,
  shopId,
  localizeMessage,
  isLoading,
  onActivate,
  showPreviewAction,
  canOpenPreview,
  onOpenPreview,
}) {
  const history = useHistory();

  if (isLoading) {
    return <div className={styles.skeleton} data-testid="checklist-loading" />;
  }

  const { steps, completedCount, total, isSetupComplete, activeStepIndex } = checklist;

  if (isSetupComplete) {
    return (
      <div className={styles.complete} data-testid="checklist-complete">
        <CheckCircle className={styles.completeIcon} />
        <span className={styles.completeText}>
          {localizeMessage('operator.checklist.complete')}
        </span>
      </div>
    );
  }

  return (
    <div className={styles.checklist} data-testid="onboarding-checklist">
      <div className={styles.header}>
        <h3 className={styles.title}>
          {localizeMessage('operator.checklist.title')}
        </h3>
        <span className={styles.progress} data-testid="checklist-progress">
          {completedCount}
          /
          {total}
        </span>
      </div>

      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${(completedCount / total) * 100}%` }}
          data-testid="checklist-progress-bar"
        />
      </div>

      <ul className={styles.steps}>
        {steps.map((step, idx) => {
          const isActive = idx === activeStepIndex;
          const previewAction = showPreviewAction && step.id === ChecklistStepId.GO_LIVE ? (
            <li
              key="preview_storefront"
              className={clsx(styles.step, styles.stepPreviewAction)}
              data-testid="checklist-preview-action"
            >
              <span className={styles.stepIcon}>
                <RadioButtonUnchecked fontSize="small" className={styles.iconPending} />
              </span>
              <span className={styles.stepLabel}>
                {localizeMessage('operator.checklist.preview_store')}
              </span>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                className={styles.stepCta}
                onClick={onOpenPreview}
                disabled={!canOpenPreview}
                data-testid="checklist-open-preview"
              >
                {localizeMessage('operator.checklist.cta_open_preview')}
              </Button>
            </li>
          ) : null;

          const handleClick = () => {
            if (step.route === ActionRoute.HOME_ACTIVATE) {
              if (onActivate) onActivate();
            } else {
              const path = resolveActionRoute(step.route, shopId);
              if (path) history.push(path);
            }
          };

          return (
            <React.Fragment key={step.id}>
              {previewAction}
              <li
                className={clsx(
                  styles.step,
                  step.complete && styles.stepComplete,
                  isActive && styles.stepActive,
                )}
                data-testid={`checklist-step-${step.id}`}
              >
                <span className={styles.stepIcon}>
                  {step.complete
                    ? <CheckCircle fontSize="small" className={styles.iconDone} />
                    : <RadioButtonUnchecked fontSize="small" className={styles.iconPending} />}
                </span>
                <span className={clsx(styles.stepLabel, step.complete && styles.stepLabelDone)}>
                  {localizeMessage(step.messageKey)}
                </span>
                {isActive && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    className={styles.stepCta}
                    onClick={handleClick}
                    data-testid={`checklist-cta-${step.id}`}
                  >
                    {localizeMessage(step.ctaKey || 'operator.checklist.cta')}
                  </Button>
                )}
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
}

OnboardingChecklist.propTypes = {
  checklist: PropTypes.shape({
    steps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        messageKey: PropTypes.string.isRequired,
        ctaKey: PropTypes.string,
        complete: PropTypes.bool.isRequired,
        route: PropTypes.string,
      }),
    ).isRequired,
    completedCount: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    isSetupComplete: PropTypes.bool.isRequired,
    activeStepIndex: PropTypes.number.isRequired,
  }).isRequired,
  shopId: PropTypes.string,
  localizeMessage: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  onActivate: PropTypes.func,
  showPreviewAction: PropTypes.bool,
  canOpenPreview: PropTypes.bool,
  onOpenPreview: PropTypes.func,
};

OnboardingChecklist.defaultProps = {
  shopId: null,
  isLoading: false,
  onActivate: null,
  showPreviewAction: false,
  canOpenPreview: false,
  onOpenPreview: null,
};
